import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Heart, Share2, Eye, Volume2, VolumeX, Film } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { getTenantSlug } from "../../lib/tenant";
import EmptyState from "../../components/EmptyState";
import { toast } from "sonner";

export default function Reels() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [reels, setReels] = useState([]);
  const [muted, setMuted] = useState(true);
  const videoRefs = useRef({});

  useEffect(() => {
    // The home screen shows reels to signed-out visitors too, and links
    // here. They read the same public storefront data; the signed-in
    // endpoint would answer 401 and send them to the login screen.
    const source = user ? "/api/customer/reels" : `/api/public/tenant/${getTenantSlug()}/home`;
    api.get(source).then(({ data }) => setReels(data.reels || []))
      .catch((e) => toast.error(friendlyMessage(e)));
  }, [user]);

  const like = async (id) => {
    if (!user) { nav(`/login?return=${encodeURIComponent("/app/reels")}`); return; }
    await api.post(`/api/customer/reels/${id}/like`);
    setReels((r) => r.map((x) => x.id === id ? { ...x, likes: x.likes + 1 } : x));
  };

  return (
    <div className="bg-black min-h-screen pb-20" data-testid="reels-page">
      <header className="fixed top-0 left-0 right-0 z-50 pt-safe">
        <div className="px-5 pt-5 pb-3 flex items-center gap-2 bg-gradient-to-b from-black/80 to-transparent">
          <Link to="/app/home" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-extrabold tracking-tight text-white flex-1">Reels</h1>
          <button onClick={() => setMuted(!muted)} className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white" data-testid="mute-btn">
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>
      </header>

      <div className="snap-y snap-mandatory overflow-y-auto h-[100dvh]">
        {reels.map((r) => (
          <div key={r.id} className="snap-start h-[100dvh] relative flex items-center justify-center" data-testid={`reel-${r.id}`}>
            <video
              ref={(el) => { if (el) videoRefs.current[r.id] = el; }}
              src={r.video_url} poster={r.thumbnail_url}
              className="w-full h-full object-cover"
              loop muted={muted} playsInline autoPlay
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none" />
            <div className="absolute bottom-24 left-5 right-20 text-white">
              <div className="font-heading text-2xl font-extrabold leading-tight">{r.title}</div>
              <div className="text-sm text-white/80 mt-1 leading-relaxed">{r.description}</div>
            </div>
            <div className="absolute bottom-24 right-5 flex flex-col gap-4">
              <button onClick={() => like(r.id)} className="flex flex-col items-center text-white" data-testid={`like-${r.id}`}>
                <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                  <Heart className="w-6 h-6" />
                </div>
                <span className="text-xs font-bold mt-1">{r.likes}</span>
              </button>
              <button className="flex flex-col items-center text-white">
                <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                  <Share2 className="w-6 h-6" />
                </div>
                <span className="text-xs font-bold mt-1">Share</span>
              </button>
              <div className="flex flex-col items-center text-white">
                <Eye className="w-6 h-6" />
                <span className="text-xs font-bold mt-1">{r.views}</span>
              </div>
            </div>
          </div>
        ))}
        {reels.length === 0 && (
          <EmptyState icon={Film} variant="dark" title="No reels yet"
                      description="Check back soon for fresh content." fullHeight className="h-screen" testId="reels-empty" />
        )}
      </div>
    </div>
  );
}
